/* eslint-disable functional/immutable-data */

import type { Cache } from '@urql/exchange-graphcache';

import type {
  AddAddressMutation,
  AddAddressSuccess,
  Address,
  BillingAccount,
  Customer,
  DeleteAddressMutation,
  DeleteAddressSuccess,
} from '@order/graphql';
import { CurrentCustomerDocument } from '@order/graphql';

/**
 * Changing addresses returns an address object.
 * This is fine for when the address is updated,
 * But if it is added or removed, this is needed to update
 * the current customer's object's addresses in URQL cache.
 */

export const addAddressCacheUpdate = (
  cache: Cache,
  result: AddAddressMutation,
) => {
  const response = result.addAddress;
  const data = response as AddAddressSuccess;
  const typename = response.__typename;

  // Ignore unsuccessful mutations.
  if (typename !== 'AddAddressSuccess') return;

  updateCacheWithAddressChanges(cache, data);
};

export const deleteAddressCacheUpdate = (
  cache: Cache,
  result: DeleteAddressMutation,
  input: { readonly addressId: string },
) => {
  const deletedId = input?.addressId;
  const data = result.deleteAddress as DeleteAddressSuccess;

  // Ignore unsuccessful mutations.
  if (!deletedId || !data?.success) return;

  updateCacheWithAddressChanges(cache, {
    address: { id: String(deletedId) },
    removed: true,
  });
};

const updateCacheWithAddressChanges = (
  customerCache: Cache,
  addressChanges: AddressChanges,
) => {
  customerCache.updateQuery({ query: CurrentCustomerDocument }, (data) => {
    if (!data) return data;

    // Get cached addresses.
    const cachedCustomer = data?.currentCustomer as Customer;
    const addresses = cachedCustomer?.addresses ?? [];
    const withoutUpdated = addresses.filter(
      ({ id }) => id !== addressChanges.address.id,
    );

    // If the address was removed, remove it from the cache.
    if (addressChanges.removed) {
      data.currentCustomer = {
        ...cachedCustomer,
        addresses: withoutUpdated,
      };

      return data;
    }

    // If the credit card was added, add it and sort by id.
    data.currentCustomer = {
      ...cachedCustomer,
      addresses: [
        ...withoutUpdated,
        addressChanges.address as BillingAccount,
      ].sort((c1, c2) => c1.id.localeCompare(c2.id)),
    };

    return data;
  });
};

type AddressChanges = {
  readonly address: Partial<Address>;
  readonly removed?: boolean;
};
